import { ChangeEvent, useEffect, useState } from "react";
import { useRouter } from "next/router";
import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { SortOrderEnum } from "@shared/lib/types/api/pagination";
import { formatDate } from "@shared/lib/utils/date/formatSchedule";
import { openToast, TOAST_TYPE } from "@shared/lib/utils/toast";
import {
  InquiryStatus,
  StudentInquiryDetail,
  WriteInquiry,
  ErrorAlert,
  FlexTable,
  FlexTableCell,
  FlexTableRow,
  InquiryTypeText,
  NoPostsText,
  ResponsiveTable,
  StyledTableRow,
  ColumnConfig,
  LoadingSpinner,
} from "@/components";

import { useAccessToken } from "@/hooks/auth/use-access-token";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

import {
  getInquiryControllerGetInquiriesForInstructorQueryKey,
  getInquiryControllerGetInquiriesForStudentQueryKey,
  useInquiryControllerCreateInquiry,
  useInquiryControllerGetInquiriesForStudent,
} from "@shared/generated/api/fn/kac/inquiry/inquiry";
import {
  CreateCourseInquiryRequestDtoContent,
  InquiryControllerGetInquiriesForInstructorTypes,
  InquiryControllerGetInquiriesForStudentTypes,
  InquiryResponseDto,
} from "@shared/generated/api/model";
import { getInstructorName } from "@/utils/inquiry";
import { useMediaQueryMaxWidth } from "@shared/lib/hooks/use-media-query-max-width";

interface CourseQnaTabProps {
  courseId: number;
  isWriting: boolean;
  onCloseWrite: () => void;
  inquiryId?: number | null;
}

export const CourseQnaTab = ({
  courseId,
  isWriting,
  onCloseWrite,
}: CourseQnaTabProps) => {
  const { t } = useCustomTranslation();
  const { isTabletSm } = useMediaQueryMaxWidth();

  const router = useRouter();
  const locale = router.locale as "ko" | "en";

  const [page, setPage] = useState(1);
  const [selectedInquiryId, setSelectedInquiryId] = useState<number | null>(
    null,
  );
  useEffect(() => {
    const inquiryId = router.query.inquiryId;
    inquiryId
      ? setSelectedInquiryId(Number(inquiryId))
      : setSelectedInquiryId(null);
  }, [router]);

  const queryClient = useQueryClient();

  const PAGE_SIZE = 10;
  const { accessToken } = useAccessToken();

  const inquiryQueryKeys = [
    getInquiryControllerGetInquiriesForStudentQueryKey(),
    courseId,
  ];

  const { data, isLoading, error } = useInquiryControllerGetInquiriesForStudent(
    {
      skip: (page - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
      courseId,
      isRoot: true,
      types: InquiryControllerGetInquiriesForInstructorTypes.COURSE_QNA,
      orderBy: { createdAt: SortOrderEnum.DESC },
    } as any, // orderBy가 생성 타입에 포함되지 않아 타입 에러 띄움
    {
      request: { accessToken },
      query: {
        enabled: !!accessToken,
        queryKey: inquiryQueryKeys,
      },
    },
  );

  const { mutateAsync: createInquiry } = useInquiryControllerCreateInquiry({
    request: { accessToken },
    mutation: {
      onSuccess: () => {
        const queryKeys = [
          getInquiryControllerGetInquiriesForStudentQueryKey(),
          getInquiryControllerGetInquiriesForInstructorQueryKey(),
        ];
        queryKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      },
    },
  });

  if (isLoading) {
    return (
      <LoadingSpinner
        className="CourseQnaTab"
        message="Loading..."
      />
    );
  }
  if (error) {
    return (
      <ErrorAlert
        className="CourseQnaTab"
        error={error}
      />
    );
  }

  const handlePageChange = (_event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleRowClick = (_event: React.MouseEvent, inquiryId: number) => {
    setSelectedInquiryId(inquiryId);
  };

  const handleClose = () => {
    setSelectedInquiryId(null);
  };

  // 글쓰기 제출
  const handleSubmit = async (formData: {
    subject: string;
    content: CreateCourseInquiryRequestDtoContent;
  }) => {
    const courseScheduleId = Number(router.query.scheduleId);
    try {
      await createInquiry({
        data: {
          courseId,
          ...(courseScheduleId ? { courseScheduleId } : {}),
          subject: formData.subject,
          content: formData.content,
          type: InquiryControllerGetInquiriesForStudentTypes.COURSE_QNA,
        },
      });
      openToast(TOAST_TYPE.SUCCESS, t("inquiry_registration_completed"));
      onCloseWrite();
    } catch (error) {
      console.error("문의 등록 실패:", error);
      openToast(TOAST_TYPE.ERROR, t("failed_to_register_inquiry"));
    }
  };

  if (selectedInquiryId) {
    return (
      <StudentInquiryDetail
        inquiryId={selectedInquiryId}
        onClose={handleClose}
      />
    );
  }

  if (isWriting) {
    return (
      <WriteInquiry
        onSubmit={handleSubmit}
        onClose={onCloseWrite}
      />
    );
  }

  const columns: ColumnConfig[] = [
    { width: 75, label: t("id") },
    { flex: 3, label: t("title") },
    { flex: 1, label: t("teacher_name") },
    { flex: 1, label: t("writer") },
    { flex: 2.5, label: t("registration_date") },
    { flex: 1, label: t("answer_status") },
  ];

  const totalPages = Math.max(Math.ceil((data?.count ?? 0) / PAGE_SIZE), 1);

  return (
    <FlexTable
      columns={columns}
      page={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
    >
      {!data?.count && <NoPostsText>{t("no_registered_posts")}</NoPostsText>}
      {data?.rows?.map((item: InquiryResponseDto, index: number) => (
        <>
          {isTabletSm ? (
            <ResponsiveTable key={item.id}>
              <StyledTableRow>
                <div className="qna">
                  <InquiryTypeText className="qna">{`[${t("class")}]`}</InquiryTypeText>
                  {item.subject}
                </div>
              </StyledTableRow>
              <StyledTableRow>
                <div>
                  <InquiryTypeText>{t("teacher_name")}</InquiryTypeText>
                  {getInstructorName(item.course, locale)}
                </div>
              </StyledTableRow>
              <StyledTableRow>
                <div>
                  <InquiryTypeText>{t("writer")}</InquiryTypeText>
                  {item.user?.name ?? ""}
                </div>
                <div>
                  <InquiryTypeText>{t("registration_date")}</InquiryTypeText>
                  {item.createdAt &&
                    Object.keys(item.createdAt).length > 0 &&
                    formatDate(new Date(item.createdAt))}
                </div>
              </StyledTableRow>
              <StyledTableRow>
                <InquiryStatus status={item.status} />
              </StyledTableRow>
            </ResponsiveTable>
          ) : (
            <FlexTableRow key={item.id}>
              <FlexTableCell width={75}>
                {data.count - (page - 1) * PAGE_SIZE - index}
              </FlexTableCell>
              <FlexTableCell flex={3}>
                <LinkText onClick={(e) => handleRowClick(e, item.id)}>
                  {item.subject}
                </LinkText>
              </FlexTableCell>
              <FlexTableCell flex={1}>
                {getInstructorName(item.course, locale)}
              </FlexTableCell>
              <FlexTableCell flex={1}>{item.user?.name ?? ""}</FlexTableCell>
              <FlexTableCell flex={2.5}>
                {item.createdAt &&
                  Object.keys(item.createdAt).length > 0 &&
                  formatDate(new Date(item.createdAt))}
              </FlexTableCell>
              <FlexTableCell flex={1}>
                <InquiryStatus status={item.status} />
              </FlexTableCell>
            </FlexTableRow>
          )}
        </>
      ))}
    </FlexTable>
  );
};

const LinkText = styled(Typography)`
  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;
